import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getDocumentSignedUrl } from '@/lib/storageUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, Eye, Download, ChevronDown, ChevronUp, FileText, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { resolveUnaForUser, renderMergeFields, createMergeData } from '@/lib/documentMerge';
import { sanitizeDocumentContent } from '@/lib/sanitize';

interface CompletedDocumentViewerProps {
  documentId: string;
  documentTitle: string;
  successMessage?: string;
  templateName?: string;
  userId?: string;
}

interface DocumentData {
  id: string;
  document_name: string;
  custom_content: string | null;
  file_url: string | null;
  file_path?: string | null;
}

export const CompletedDocumentViewer = ({ 
  documentId, 
  documentTitle,
  successMessage = 'Your document has been signed and filed.',
  templateName,
  userId
}: CompletedDocumentViewerProps) => {
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      // If we have a documentId, fetch from generated_documents
      if (documentId) {
        const { data, error } = await supabase
          .from('generated_documents')
          .select('id, document_name, custom_content, file_url, file_path')
          .eq('id', documentId)
          .single();

        if (!error && data) {
          setDocument(data as DocumentData);
          setLoading(false);
          return;
        }
      }

      // Fallback: fetch template content if no documentId but we have templateName
      if (templateName) {
        const { data: template } = await supabase
          .from('document_templates')
          .select('template_content')
          .eq('template_name', templateName)
          .eq('is_active', true)
          .maybeSingle();

        if (template) {
          // Resolve UNA to populate merge fields
          let unaName = '[Your UNA]';
          if (userId) {
            const unaContext = await resolveUnaForUser(userId);
            if (unaContext) {
              unaName = unaContext.unaName;
            }
          }
          
          const mergeData = createMergeData(unaName);
          const renderedContent = renderMergeFields(template.template_content, mergeData);
          
          setDocument({
            id: '',
            document_name: documentTitle,
            custom_content: renderedContent,
            file_url: null,
          });
        }
      }

      setLoading(false);
    };

    fetchDocument();
  }, [documentId, templateName, documentTitle]);

  if (loading) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading document...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/50">
      <CardContent className="pt-6">
        <div className="flex items-start gap-3 mb-4">
          <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-green-800 text-sm sm:text-base">{documentTitle} Signed</h3>
            <p className="text-xs sm:text-sm text-green-700">{successMessage}</p>
          </div>
        </div>

        {document?.custom_content && (
          <Collapsible open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm" className="w-full justify-between mb-3 h-auto py-2">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 flex-shrink-0" />
                  <span className="text-left">{isPreviewOpen ? 'Hide Document Preview' : 'Show Document Preview'}</span>
                </span>
                {isPreviewOpen ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border rounded-md bg-white mb-3 overflow-hidden">
                <ScrollArea className="h-[200px]">
                  <div 
                    className="p-6 ecclesiastical-document max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(document.custom_content) }}
                  />
                </ScrollArea>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          {document?.custom_content && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <Eye className="h-4 w-4 flex-shrink-0" />
              <span>View Full Document</span>
            </Button>
          )}
          {(document?.file_path || document?.file_url) && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={async () => {
                const url = await getDocumentSignedUrl(document);
                if (url) window.open(url, '_blank');
              }}
            >
              <Download className="h-4 w-4 flex-shrink-0" />
              <span>Download PDF</span>
            </Button>
          )}
        </div>

        {/* Full Document Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{document?.document_name || documentTitle}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="h-[70vh]">
              <div 
                className="p-6 ecclesiastical-document max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(document?.custom_content || '') }}
              />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
